import React, { useState, useEffect, useCallback } from 'react';
import './ManagerAppointments.css';
import WeekCalendar from './WeekCalendar';
import { startOfWeek, addDays, ymd } from '../utils/appointmentWeek';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:8000';

const BLANK_FORM = { rep: '', date: '', time: '', customer_name: '', notes: '' };

const pad2 = (n) => String(n).padStart(2, '0');

function todayYMD() {
  const d = new Date();
  return `${d.getFullYear()}-${pad2(d.getMonth() + 1)}-${pad2(d.getDate())}`;
}

function splitDateTime(dtStr) {
  if (!dtStr) return { date: '', time: '' };
  const d = new Date(dtStr);
  if (isNaN(d)) return { date: '', time: '' };
  const date = `${d.getFullYear()}-${pad2(d.getMonth() + 1)}-${pad2(d.getDate())}`;
  const time = formatParsedTime({ hours: d.getHours(), minutes: d.getMinutes() });
  return { date, time };
}

// Smart free-text time parsing for a 7 AM–7 PM business day. The user can type
// "3", "3:30", "315", "14:30", "3pm", "8 am", etc. When no AM/PM is given we
// assume business hours: 1–6 -> PM, 8–11 -> AM, 12 -> noon, 7 -> AM, 13–23 are
// already 24h. Returns { hours, minutes } (24h) or null.
function parseSmartTime(raw) {
  if (!raw) return null;
  let s = String(raw).trim().toUpperCase().replace(/\./g, '');
  let mer = null;
  if (s.endsWith('AM')) { mer = 'AM'; s = s.slice(0, -2); }
  else if (s.endsWith('PM')) { mer = 'PM'; s = s.slice(0, -2); }
  else if (s.endsWith('A')) { mer = 'AM'; s = s.slice(0, -1); }
  else if (s.endsWith('P')) { mer = 'PM'; s = s.slice(0, -1); }
  s = s.replace(/\s+/g, '');

  let hours, minutes;
  let m = s.match(/^(\d{1,2}):(\d{2})$/);
  if (m) {
    hours = parseInt(m[1]); minutes = parseInt(m[2]);
  } else if ((m = s.match(/^(\d{1,2})$/))) {
    hours = parseInt(m[1]); minutes = 0;
  } else if ((m = s.match(/^(\d{3,4})$/))) {            // "315" -> 3:15, "1430" -> 14:30
    minutes = parseInt(m[1].slice(-2));
    hours = parseInt(m[1].slice(0, -2));
  } else {
    return null;
  }
  if (minutes > 59) return null;

  if (mer === 'AM') {
    if (hours === 12) hours = 0;
  } else if (mer === 'PM') {
    if (hours !== 12) hours += 12;
  } else {
    // No AM/PM given — assume a 7 AM–7 PM business day.
    if (hours >= 1 && hours <= 6) hours += 12;          // 1–6 -> 1 PM–6 PM
    // 7 -> 7 AM, 8–11 -> AM, 12 -> noon, 13–23 -> already 24h (left as-is)
  }
  if (hours < 0 || hours > 23) return null;
  return { hours, minutes };
}

function formatParsedTime(t) {
  if (!t) return '';
  const ampm = t.hours >= 12 ? 'PM' : 'AM';
  const h12 = t.hours % 12 || 12;
  return `${h12}:${pad2(t.minutes)} ${ampm}`;
}

function parseTimeToISO(dateStr, timeStr) {
  if (!dateStr) return null;
  const t = parseSmartTime(timeStr);
  if (!t) return null;
  return `${dateStr}T${pad2(t.hours)}:${pad2(t.minutes)}:00`;
}

const ManagerAppointments = ({ onBack, user }) => {
  const [appointments, setAppointments] = useState([]);
  const [reps, setReps] = useState([]);
  const [weekStart, setWeekStart] = useState(() => startOfWeek(new Date()));
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState(BLANK_FORM);
  const [editId, setEditId] = useState(null);
  const [saving, setSaving] = useState(false);
  const [formError, setFormError] = useState('');

  const headers = { 'Content-Type': 'application/json', 'X-User-ID': String(user.userId) };

  const loadReps = useCallback(async () => {
    try {
      const res = await fetch(`${API_URL}/api/manager/appointments/reps/`, { headers });
      if (res.ok) setReps(await res.json());
    } catch (e) {
      console.error('Failed to load reps', e);
    }
  }, [user.userId]); // eslint-disable-line react-hooks/exhaustive-deps

  const loadAppointments = useCallback(async () => {
    setLoading(true);
    setError('');
    const start = ymd(weekStart);
    const end = ymd(addDays(weekStart, 6));
    try {
      const res = await fetch(
        `${API_URL}/api/manager/appointments/?start=${start}&end=${end}`,
        { headers }
      );
      if (!res.ok) throw new Error('Failed to load appointments');
      setAppointments(await res.json());
    } catch (e) {
      setError('Failed to load appointments.');
    } finally {
      setLoading(false);
    }
  }, [weekStart, user.userId]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => { loadReps(); }, [loadReps]);
  useEffect(() => { loadAppointments(); }, [loadAppointments]);

  const goPrev = () => setWeekStart((w) => addDays(w, -7));
  const goNext = () => setWeekStart((w) => addDays(w, 7));
  const goToday = () => setWeekStart(startOfWeek(new Date()));

  const openAdd = () => {
    setEditId(null);
    setForm({ ...BLANK_FORM, date: todayYMD() });
    setFormError('');
    setShowForm(true);
  };

  const openEdit = (appt) => {
    setEditId(appt.id);
    const { date, time } = splitDateTime(appt.appointment_datetime);
    setForm({
      rep: String(appt.rep),
      date,
      time,
      customer_name: appt.customer_name,
      notes: appt.notes || '',
    });
    setFormError('');
    setShowForm(true);
  };

  const closeForm = () => {
    setShowForm(false);
    setEditId(null);
    setForm(BLANK_FORM);
    setFormError('');
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.rep) { setFormError('Please select a rep.'); return; }
    if (!form.date) { setFormError('Please select a date.'); return; }
    if (!form.time) { setFormError('Please choose a time.'); return; }
    if (!form.customer_name.trim()) { setFormError('Please enter a customer name.'); return; }

    const isoDatetime = parseTimeToISO(form.date, form.time);
    if (!isoDatetime) {
      setFormError('Please choose a valid time.');
      return;
    }

    setSaving(true);
    setFormError('');
    try {
      const body = {
        rep: parseInt(form.rep),
        appointment_datetime: isoDatetime,
        customer_name: form.customer_name.trim(),
        notes: form.notes.trim(),
      };
      const url = editId
        ? `${API_URL}/api/manager/appointments/${editId}/`
        : `${API_URL}/api/manager/appointments/`;
      const res = await fetch(url, {
        method: editId ? 'PUT' : 'POST',
        headers,
        body: JSON.stringify(body),
      });
      if (!res.ok) {
        const data = await res.json();
        throw new Error(data.error || 'Save failed');
      }
      closeForm();
      loadAppointments();
    } catch (e) {
      setFormError(e.message || 'Failed to save.');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this appointment?')) return;
    try {
      const res = await fetch(`${API_URL}/api/manager/appointments/${id}/`, {
        method: 'DELETE', headers,
      });
      if (!res.ok) throw new Error();
      setAppointments((prev) => prev.filter((a) => a.id !== id));
    } catch {
      alert('Failed to delete appointment.');
    }
  };

  return (
    <div className="ma-container">
      <div className="ma-header">
        <button className="ma-back-btn" onClick={onBack}>Back</button>
        <h2>My Field Appointments</h2>
        <button className="ma-add-btn" onClick={openAdd}>+ Add Appointment</button>
      </div>

      {error && <div className="ma-error">{error}</div>}

      {loading ? (
        <div className="ma-loading">Loading...</div>
      ) : (
        <WeekCalendar
          weekStart={weekStart}
          appointments={appointments}
          onPrev={goPrev}
          onNext={goNext}
          onToday={goToday}
          editable
          onEdit={openEdit}
          onDelete={handleDelete}
        />
      )}

      {showForm && (
        <div className="ma-modal-overlay" onClick={closeForm}>
          <div className="ma-modal" onClick={(e) => e.stopPropagation()}>
            <button className="ma-modal-close" onClick={closeForm}>×</button>
            <h3>{editId ? 'Edit Appointment' : 'Log Appointment'}</h3>
            <form onSubmit={handleSubmit} className="ma-form">
              <label className="ma-form-label">Rep</label>
              <select
                className="ma-form-input"
                value={form.rep}
                onChange={(e) => setForm({ ...form, rep: e.target.value })}
                required
              >
                <option value="">Select a rep...</option>
                {reps.map((r) => (
                  <option key={r.id} value={r.id}>
                    {r.full_name || r.username}
                  </option>
                ))}
              </select>

              <label className="ma-form-label">Date</label>
              <input
                type="date"
                className="ma-form-input"
                value={form.date}
                onChange={(e) => setForm({ ...form, date: e.target.value })}
                required
              />

              <label className="ma-form-label">Time</label>
              <input
                type="text"
                inputMode="numeric"
                className="ma-form-input"
                placeholder="e.g. 3, 3:30, or 8 AM"
                value={form.time}
                onChange={(e) => setForm({ ...form, time: e.target.value })}
                required
              />
              {form.time.trim() !== '' && (
                parseSmartTime(form.time)
                  ? <div className="ma-time-preview">Saved as <strong>{formatParsedTime(parseSmartTime(form.time))}</strong></div>
                  : <div className="ma-time-preview ma-time-bad">Type a time like 3, 3:30, or 8 AM</div>
              )}

              <label className="ma-form-label">Customer Name</label>
              <input
                type="text"
                className="ma-form-input"
                placeholder="Customer / account name"
                value={form.customer_name}
                onChange={(e) => setForm({ ...form, customer_name: e.target.value })}
                required
              />

              <label className="ma-form-label">Notes <span className="ma-optional">(optional)</span></label>
              <textarea
                className="ma-form-input ma-form-textarea"
                placeholder="Any additional notes..."
                value={form.notes}
                onChange={(e) => setForm({ ...form, notes: e.target.value })}
                rows={3}
              />

              {formError && <div className="ma-form-error">{formError}</div>}

              <div className="ma-form-actions">
                <button type="button" className="ma-cancel-btn" onClick={closeForm} disabled={saving}>
                  Cancel
                </button>
                <button type="submit" className="ma-save-btn" disabled={saving}>
                  {saving ? 'Saving...' : editId ? 'Save Changes' : 'Log Appointment'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default ManagerAppointments;
